import type { Client } from '../api/client.js';
import type { QuestionBeta, ReglageAppris, RoleCode } from '../api/contrat.js';
import { COULEURS, styleBouton } from './styles.js';

/**
 * Hôte des pop-ups « BETA test » (UX-17) : interroge `/beta/questions?contexte=` au moment où un écran s’ouvre,
 * pose UNE question à la fois, jamais pendant une saisie ni un appel, jamais à un client. « Plus tard » remet la
 * question à la prochaine session ; la réponse devient un réglage appris, modifiable dans « Réglages appris ».
 */
const AUDIENCE: readonly RoleCode[] = ['admin', 'dirigeante', 'assistante'];

export const recoitBeta = (role: RoleCode): boolean => AUDIENCE.includes(role);

/** Le contexte interrogé est l’identifiant de l’écran qui vient de s’ouvrir. */
export const contexteBeta = (ecranId: string): string => ecranId;

export interface ConditionsBeta {
  saisieEnCours: boolean;
  appelEnCours: boolean;
  reportees: readonly string[];
}

export function choisirQuestion(questions: readonly QuestionBeta[], conditions: ConditionsBeta): QuestionBeta | null {
  if (conditions.saisieEnCours || conditions.appelEnCours) return null;
  return questions.find((q) => !conditions.reportees.includes(q.key)) ?? null;
}

export interface EtatBeta {
  questions: readonly QuestionBeta[];
  reportees: readonly string[];
}

export type ActionBeta =
  | { type: 'recues'; questions: readonly QuestionBeta[] }
  | { type: 'plus-tard'; cle: string }
  | { type: 'repondue'; cle: string }
  | { type: 'ecran-change' };

export const ETAT_BETA_INITIAL: EtatBeta = { questions: [], reportees: [] };

export function reducerBeta(etat: EtatBeta, action: ActionBeta): EtatBeta {
  switch (action.type) {
    case 'recues':
      return { ...etat, questions: action.questions };
    case 'plus-tard':
      return { ...etat, reportees: etat.reportees.includes(action.cle) ? etat.reportees : [...etat.reportees, action.cle] };
    case 'repondue':
      return { ...etat, questions: etat.questions.filter((q) => q.key !== action.cle) };
    case 'ecran-change':
      return { ...etat, questions: [] };
  }
}

export async function questionsDues(client: Client, contexte: string): Promise<QuestionBeta[]> {
  try {
    return await client.appeler<QuestionBeta[]>('beta.questionsDues', { requete: { contexte } });
  } catch {
    // Une question BETA ne doit jamais gêner le travail : en cas de panne, on ne pose rien.
    return [];
  }
}

export function repondreBeta(client: Client, question: QuestionBeta, option: string, ecranId: string): Promise<ReglageAppris> {
  return client.appeler<ReglageAppris>('beta.repondre', {
    parametres: { cle: question.key },
    corps: { option, context: { ecran: ecranId } },
  });
}

export interface PropsFenetreBeta {
  question: QuestionBeta;
  onRepondre: (option: string) => void;
  onPlusTard: () => void;
}

type CibleDom = { currentTarget?: { closest?: (s: string) => ParentNode | null } };

function elementsBeta(e: CibleDom): { dialogue: HTMLElement | null | undefined; pastille: HTMLElement | null | undefined } {
  const racine = e.currentTarget?.closest?.('[data-beta]');
  return {
    dialogue: racine?.querySelector<HTMLElement>('[role="dialog"]'),
    pastille: racine?.querySelector<HTMLElement>('[data-beta-pastille]'),
  };
}

/**
 * Réduit la pop-up en pastille (état porté par le DOM, comme le menu mobile : la fenêtre reste une fonction pure du routeur).
 * La question reste posée : réduire n'enregistre rien et ne reporte rien.
 */
function reduire(e: CibleDom): void {
  const { dialogue, pastille } = elementsBeta(e);
  if (dialogue) dialogue.hidden = true;
  if (pastille) {
    pastille.hidden = false;
    pastille.focus();
  }
}

function rouvrir(e: CibleDom): void {
  const { dialogue, pastille } = elementsBeta(e);
  if (pastille) pastille.hidden = true;
  if (dialogue) {
    dialogue.hidden = false;
    dialogue.querySelector<HTMLElement>('button')?.focus();
  }
}

/**
 * Pop-up BETA du prototype de démonstration (décision de Joel, 30/10) : NON modale, ancrée en bas à droite, repliable. Elle ne masque
 * plus l'écran : le travail continue derrière. Accessible : `role="dialog"` sans `aria-modal`, tous les boutons atteignables,
 * Échap la réduit.
 */
export function FenetreBeta({ question, onRepondre, onPlusTard }: PropsFenetreBeta) {
  const titre = `beta-${question.id}`;
  return (
    <div
      data-beta=""
      style={{ position: 'fixed', right: 16, bottom: 16, zIndex: 30, width: 'min(400px, calc(100vw - 32px))', maxHeight: '60vh' }}
    >
      <div
        role="dialog"
        aria-labelledby={titre}
        onKeyDown={(e) => {
          if (e.key === 'Escape') reduire(e);
        }}
        style={{
          boxSizing: 'border-box',
          maxHeight: '60vh',
          overflowY: 'auto',
          background: COULEURS.blanc,
          color: COULEURS.marineFonce,
          border: `2px solid ${COULEURS.indigo}`,
          borderRadius: 12,
          boxShadow: '0 1px 2px rgba(38, 42, 78, 0.06), 0 12px 40px rgba(38, 42, 78, 0.25)',
          padding: 16,
          lineHeight: 1.5,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <span
            style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 999, background: COULEURS.indigo, color: COULEURS.blanc, fontSize: 14, fontWeight: 600 }}
          >
            BETA test
          </span>
          <button type="button" onClick={reduire} style={{ ...styleBouton, border: '1px solid transparent', color: COULEURS.indigo, textDecoration: 'underline' }}>
            Réduire
          </button>
        </div>
        <h2 id={titre} style={{ fontSize: 18, margin: '8px 0' }}>
          {question.question}
        </h2>
        <div style={{ display: 'grid', gap: 8 }}>
          {question.options.map((option) => (
            <button key={option.code} type="button" onClick={() => onRepondre(option.code)} style={{ ...styleBouton, textAlign: 'left' }}>
              {option.label}
              {option.code === question.default ? ' (proposée)' : ''}
            </button>
          ))}
          <button type="button" onClick={onPlusTard} style={{ ...styleBouton, border: '1px solid transparent', color: COULEURS.ardoise }}>
            Plus tard
          </button>
        </div>
        <p style={{ color: COULEURS.ardoise, fontSize: 14, marginBottom: 0 }}>
          Votre réponse sera mémorisée. Vous pourrez la modifier à tout moment dans Réglages appris.
        </p>
      </div>
      <button
        type="button"
        data-beta-pastille=""
        hidden
        onClick={rouvrir}
        style={{ ...styleBouton, borderColor: COULEURS.indigo, color: COULEURS.indigo, boxShadow: '0 4px 16px rgba(38, 42, 78, 0.12)', marginLeft: 'auto' }}
      >
        Question BETA
      </button>
    </div>
  );
}

export interface PropsHoteBeta extends ConditionsBeta {
  role: RoleCode;
  questions: readonly QuestionBeta[];
  onRepondre: (question: QuestionBeta, option: string) => void;
  onPlusTard: (question: QuestionBeta) => void;
}

export function HoteBeta({ role, questions, saisieEnCours, appelEnCours, reportees, onRepondre, onPlusTard }: PropsHoteBeta) {
  if (!recoitBeta(role)) return null;
  const question = choisirQuestion(questions, { saisieEnCours, appelEnCours, reportees });
  if (!question) return null;
  return <FenetreBeta question={question} onRepondre={(option) => onRepondre(question, option)} onPlusTard={() => onPlusTard(question)} />;
}
